/* eslint-disable @typescript-eslint/no-unused-vars */
import {performLogout} from "@/utils/logout";
import type {Middleware, MiddlewareAPI} from "@reduxjs/toolkit";
import {isRejectedWithValue} from "@reduxjs/toolkit";
import {toast} from "sonner";

interface ErrorDetail {
  loc?: string[];
  msg?: string;
}

interface ErrorData {
  detail?: ErrorDetail[] | string;
  message?: string;
}

interface Error {
  data?: ErrorData;
  message?: string;
  error?: {message?: string};
}

const getErrorMessage = (error: Error): string => {
  if (Array.isArray(error?.data?.detail)) {
    return error?.data?.detail
      .map((obj) => {
        if (obj?.loc) {
          return `${obj?.loc?.join(".")} ${obj?.msg}`;
        }

        return obj?.msg ?? "";
      })
      .join("\n");
  } else if (typeof error?.data?.detail === "string") {
    return error?.data?.detail;
  } else if (error?.message || error?.data?.message || error?.error?.message) {
    return (
      error?.message ||
      error?.data?.message ||
      error?.error?.message ||
      "An unknown error occurred"
    );
  } else if (typeof error === "string") {
    return error;
  }

  return "An unknown error occurred";
};

/**
 * Middleware for handling RTK Query errors and displaying them as toasts
 */
export const rtkQueryErrorMiddleware: Middleware =
  (_api: MiddlewareAPI) => (next) => (action) => {
    // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these matchers
    if (isRejectedWithValue(action)) {
      // Handle different types of errors
      const payload = action.payload as {status: number; message: string};
      switch (payload.status) {
        case 401:
          performLogout();
          break;
        default:
          toast.error(
            getErrorMessage(action.payload as Error) || "Something went wrong."
          );
      }

      if (import.meta.env.DEV) {
        console.error("RTK Query Error:", action.payload);
      }
    }

    return next(action);
  };
